import React, { useEffect, useRef, useState } from 'react';
import '../utils/pixel-scroll.css';
import { Canvg } from 'canvg';
import { glyphPaths } from '../icons/icon-paths';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const IMAGE_SRC1 = 'img/glyph_chars/glyph_char1.png';
const IMAGE_SRC2 = 'img/glyph_chars/glyph_char3.png';
const IMAGE_SRC3 = 'img/glyph_chars/glyph_char2.png';

const GLYPH_SIZE = 24;
const G11_FILL_SPEED = 1;
const G11_TO_STAGE1_SPEED = 40;
const STAGE_TRANSITION_SPEED = 20;
const STAGE2_PLUS_SPEED = 40; // Speed for stage 2 → 3/4/5 transitions
const STAGE3_TO_5_SPEED = 100; // Speed for transitions between stages 3 <-> 4 <-> 5

const iconIds = [
  'g01', 'g02', 'g03', 'g04', 'g05',
  'g06', 'g07', 'g08', 'g09', 'g10', 'g11'
];

export default function GlyphCanvas() {
  const canvasRef = useRef(null);
  const glyphImgsRef = useRef({});
  const glyphStateRef = useRef([]);
  const targetGridRef = useRef(null);
  const animationRef = useRef(null);
  const triggerRef = useRef(null);
  const stickyContainerRef = useRef(null);
  const currentStageRef = useRef(0); // Track current stage in ref for immediate access
  const scrollTriggerSetupRef = useRef(false); // Track if ScrollTrigger is already set up

  const [grids, setGrids] = useState({});
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [glyphGrid, setGlyphGrid] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);


  const setTargetStage = (stage, instant = false, targetStageIndex = null, source = 'unknown') => {
    if (stage === 'g11' && currentStageRef.current === 0) {
      // Special handling for blank → g11 (stage 1→2) - use fast random fill
      animateGlyphsToG11(source);
    } else if (stage === 'stage2') {
      // Special handling for stage 2 - use dedicated animation
      animateToStage2(source);
    } else if (targetGridRef.current && grids[stage]) {
      targetGridRef.current = grids[stage];
      // Use the same animation system for both scroll and button triggers
      animateGlyphsToPosition(targetStageIndex, source);
    } else if (stage === 'blank' && grids.blank) {
      // Handle blank stage with animation
      targetGridRef.current = grids.blank;
      // Use the same animation system for both scroll and button triggers
      animateGlyphsToPosition(targetStageIndex, source);
    }
  };

  const animateGlyphsToG11 = (source) => {
    if (!glyphStateRef.current || glyphStateRef.current.length === 0) {
      console.warn('Current glyph state not available, skipping animation to g11.');
      return;
    }

    setAnimationPhase('toG11');

    const grid = glyphStateRef.current;
    const totalCells = grid.length * grid[0].length;

    // Create array of all cell positions
    const allPositions = [];
    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        allPositions.push({ x, y });
      }
    }

    // Shuffle positions for random order
    for (let i = allPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
    }

    const CELLS_PER_STEP = Math.max(1, Math.floor(totalCells / 100));
    let currentIndex = 0;

    const animate = () => {
      if (currentIndex >= totalCells) {
        // Final fill - ensure everything is g11
        const filledGrid = grid.map(row => row.map(() => iconIds.length - 1));
        setGlyphGrid(filledGrid);
        setAnimationPhase('idle');

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      // Create new grid based on current state
      const newGrid = glyphStateRef.current.map(row => [...row]);

      // Fill the next batch of cells
      for (let i = 0; i < CELLS_PER_STEP && currentIndex < totalCells; i++) {
        const pos = allPositions[currentIndex];
        newGrid[pos.y][pos.x] = iconIds.length - 1; // g11
        currentIndex++;
      }

      setGlyphGrid(newGrid);
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(animate, G11_FILL_SPEED);
  };

  const animateGlyphsToPosition = (targetStageIndex, source) => {
    if (!targetGridRef.current) {
      console.warn('Target grid not available, skipping animation to position.');
      return;
    }

    setAnimationPhase('toPosition');
    const maxSteps = iconIds.length - 1;
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= maxSteps || !targetGridRef.current) {
        setGlyphGrid(targetGridRef.current);
        setAnimationPhase('idle');

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((targetIndex, x) => {
          const currentIndex = glyphStateRef.current[y]?.[x] || 0;
          
          // Handle both upward and downward transitions
          if (currentIndex < targetIndex) {
            // Animate up to target
            return Math.min(currentIndex + 1, targetIndex);
          } else if (currentIndex > targetIndex) {
            // Animate down to target
            return Math.max(currentIndex - 1, targetIndex);
          } else {
            // Already at target
            return currentIndex;
          }
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Use different speeds based on transition type
    const isG11Transition = targetGridRef.current === grids.g11;
    
    // Use the passed targetStageIndex if available, otherwise fall back to currentStageIndex
    const stageIndexForSpeed = targetStageIndex !== null ? targetStageIndex : currentStageRef.current;
    
    // Determine speed based on target stage
    let animationSpeed;
    if (isG11Transition) {
      animationSpeed = G11_TO_STAGE1_SPEED;
    } else if (stageIndexForSpeed === 3) {
      // Transitioning TO stage 2 (stage index 3)
      animationSpeed = STAGE_TRANSITION_SPEED; // Use faster speed for stage 2
    } else if (stageIndexForSpeed >= 2 && currentStageRef.current >= 2) {
      // Check if this is a transition BETWEEN stages 2, 3, 4 (stage1, stage2, stage3)
      // This means both current and target stages are >= 2
      if ((currentStageRef.current === 2 && stageIndexForSpeed === 3) ||  // stage1 → stage2
          (currentStageRef.current === 2 && stageIndexForSpeed === 4) ||  // stage1 → stage3
          (currentStageRef.current === 3 && stageIndexForSpeed === 2) ||  // stage2 → stage1
          (currentStageRef.current === 3 && stageIndexForSpeed === 4) ||  // stage2 → stage3
          (currentStageRef.current === 4 && stageIndexForSpeed === 2) ||  // stage3 → stage1
          (currentStageRef.current === 4 && stageIndexForSpeed === 3)) {  // stage3 → stage2
        animationSpeed = STAGE3_TO_5_SPEED;
      } else {
        animationSpeed = STAGE_TRANSITION_SPEED;
      }
    } else {
      // Transitioning TO stages 0, 1 (blank, g11) or FROM stages 0, 1
      animationSpeed = STAGE_TRANSITION_SPEED;
    }
    
    animationRef.current = setInterval(animate, animationSpeed);
  };

  const animateGlyphsToPositionFast = () => {
    if (!targetGridRef.current) {
      console.warn('Target grid not available, skipping fast animation to position.');
      return;
    }

    setAnimationPhase('toPositionFast');
    const maxSteps = iconIds.length - 1;
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= maxSteps || !targetGridRef.current) {
        setGlyphGrid(targetGridRef.current);
        setAnimationPhase('idle');

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((targetIndex, x) => {
          const currentIndex = glyphStateRef.current[y]?.[x] || 0;
          
          // Handle both upward and downward transitions
          if (currentIndex < targetIndex) {
            // Animate up to target
            return Math.min(currentIndex + 1, targetIndex);
          } else if (currentIndex > targetIndex) {
            // Animate down to target
            return Math.max(currentIndex - 1, targetIndex);
          } else {
            // Already at target
            return currentIndex;
          }
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Use same speed for g11 transitions in both directions, different speed for stage transitions
    const isG11Transition = targetGridRef.current === grids.g11;
    const animationSpeed = isG11Transition ? G11_TO_STAGE1_SPEED : STAGE_TRANSITION_SPEED;
    animationRef.current = setInterval(animate, animationSpeed);
  };

  const drawToBufferAndBlit = (grid) => {
    if (!grid || !canvasRef.current) return;
    const mainCanvas = canvasRef.current;
    const width = mainCanvas.width;
    const height = mainCanvas.height;

    const buffer = document.createElement('canvas');
    buffer.width = width;
    buffer.height = height;
    const bctx = buffer.getContext('2d');

    // Calculate proper scale to fill height while maintaining aspect ratio
    const gridWidth = grid[0].length;
    const gridHeight = grid.length;
    const gridAspectRatio = gridWidth / gridHeight;
    const canvasAspectRatio = width / height;
    
    let scale;
    if (gridAspectRatio > canvasAspectRatio) {
      // Grid is wider than canvas - scale to fit height, overflow width
      scale = height / gridHeight;
    } else {
      // Grid is taller than canvas - scale to fit width, overflow height
      scale = width / gridWidth;
    }
    
    const cellSize = scale;
    
    // Center the grid
    const scaledGridWidth = gridWidth * scale;
    const scaledGridHeight = gridHeight * scale;
    const offsetX = (width - scaledGridWidth) / 2;
    const offsetY = (height - scaledGridHeight) / 2;

    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        const glyphIndex = grid[y][x];

        // Skip drawing if the glyph is empty (null or undefined)
        if (glyphIndex === null || glyphIndex === undefined) {
          continue;
        }

        const glyph = glyphImgsRef.current[iconIds[glyphIndex]];
        if (glyph) {
          bctx.drawImage(glyph, offsetX + (x * cellSize), offsetY + (y * cellSize), cellSize, cellSize);
        }
      }
    }

    const ctx = mainCanvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0);

    glyphStateRef.current = grid;
  };

  useEffect(() => {
    const cacheGlyphBitmaps = async () => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = GLYPH_SIZE;
      tempCanvas.height = GLYPH_SIZE;
      const ctx = tempCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;

      for (const id of iconIds) {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" fill="#3520D6" width="${GLYPH_SIZE}" height="${GLYPH_SIZE}" viewBox="0 0 ${GLYPH_SIZE} ${GLYPH_SIZE}">${glyphPaths[id]}</svg>`;
        const v = await Canvg.fromString(ctx, svg);
        await v.render();

        const outCanvas = document.createElement('canvas');
        outCanvas.width = GLYPH_SIZE;
        outCanvas.height = GLYPH_SIZE;
        outCanvas.getContext('2d').drawImage(tempCanvas, 0, 0);
        glyphImgsRef.current[id] = outCanvas;
        ctx.clearRect(0, 0, GLYPH_SIZE, GLYPH_SIZE);
      }
    };

    cacheGlyphBitmaps();
  }, []);

  const processImage = src => new Promise(resolve => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, w, h).data;
      const grid = [];
      for (let y = 0; y < h; y++) {
        const row = [];
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          const index = Math.floor((brightness / 255) * (iconIds.length - 1));
          row.push(index);
        }
        grid.push(row);
      }
      resolve(grid);
    };
  });

  useEffect(() => {
    Promise.all([
      processImage(IMAGE_SRC1),
      processImage(IMAGE_SRC2),
      processImage(IMAGE_SRC3),
    ]).then(([g1, g2, g3]) => {
      const rows = g1.length;
      const cols = g1[0].length;
      const g11Grid = Array.from({ length: rows }, () => Array(cols).fill(iconIds.length - 1));
      const blankGrid = Array.from({ length: rows }, () => Array(cols).fill(null));
      
      const gridData = { blank: blankGrid, g11: g11Grid, stage1: g1, stage2: g2, stage3: g3 };
      setGrids(gridData);
      
      // Initialize with blank grid
      const initialGrid = blankGrid.map(row => [...row]);
      setGlyphGrid(initialGrid);
      targetGridRef.current = blankGrid;
      setCurrentStageIndex(0); // Start on stage 1 (blank)
      
      // Auto-transition to stage 2 after a brief delay (load-in animation)
      setTimeout(() => {
        setCurrentStageIndex(1);
        setTargetStage('g11');
      }, 150);
    });
  }, []);

  useEffect(() => {
    if (glyphGrid) {
      drawToBufferAndBlit(glyphGrid);
    }
  }, [glyphGrid]);

  useEffect(() => {
    if (!grids.stage1) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const isMobile = window.innerWidth <= 768;
    const dpr = isMobile ? 1 : (window.devicePixelRatio || 1);

    const updateCanvasSize = () => {
      // Let CSS handle the canvas sizing with viewport heights
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      // Force mobile height to 75vh, let width be natural
      const isMobile = window.innerWidth <= 768;
      let canvasWidth, canvasHeight;
      
      if (isMobile) {
        // On mobile, force 75vh height, width from CSS
        canvasHeight = window.innerHeight * 0.75;
        const rect = canvas.getBoundingClientRect();
        canvasWidth = rect.width;
      } else {
        // On desktop, get size from CSS
        const rect = canvas.getBoundingClientRect();
        canvasWidth = rect.width;
        canvasHeight = rect.height;
      }
      
      // Set canvas dimensions
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      // Redraw the current glyphs after resizing
      if (glyphStateRef.current && glyphStateRef.current.length > 0) {
        drawToBufferAndBlit(glyphStateRef.current);
      }
    };

    // Initial size update
    updateCanvasSize();

    // Add resize listener
    const handleResize = () => {
      updateCanvasSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [grids.stage1]); // Add grids.stage1 dependency so it runs when grids are loaded

  // Set CSS custom property for viewport height
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);
    
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Refresh ScrollTrigger when canvas size changes
  useEffect(() => {
    const handleResize = () => {
      // Small delay to ensure canvas has updated
      setTimeout(() => {
        setupScrollTrigger(); // Recalculate with new viewport height
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Remove grids dependency - it was causing infinite re-renders

  useEffect(() => {
    if (grids.stage1 && !scrollTriggerSetupRef.current) {
      setupScrollTrigger();
      scrollTriggerSetupRef.current = true;
    }
  }, [grids.stage1]); // Only depend on grids.stage1, not the entire grids object

  // Cleanup ScrollTrigger on unmount
  useEffect(() => {
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const setupScrollTrigger = () => {
    if (!grids.stage1 || !triggerRef.current || !stickyContainerRef.current) return;

    // Kill any existing ScrollTrigger instances
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());

    // Create a single ScrollTrigger with clear stage detection
    ScrollTrigger.create({
      trigger: triggerRef.current,
      start: "top top",
      end: "bottom top",
      onUpdate: (self) => {
        const progress = self.progress;
        setScrollProgress(progress);
        
        // Clear stage detection logic
        let newStageIndex;
        if (progress >= 0.9) newStageIndex = 0;    
        else if (progress >= 0.7) newStageIndex = 1;    // stage3
        else if (progress >= 0.32) newStageIndex = 4;    // stage3
        else if (progress >= 0.17) newStageIndex = 3;  // stage2  
        else if (progress >= 0.01) newStageIndex = 2;  // stage1
        else newStageIndex = 1;                        // blank
        
        // Only trigger if stage actually changed
        if (newStageIndex !== currentStageRef.current) {
          const stageMap = ['blank', 'g11', 'stage1', 'stage2', 'stage3'];
          const targetStage = stageMap[newStageIndex];
          
          // Update ref immediately to prevent multiple triggers
          currentStageRef.current = newStageIndex;
          setCurrentStageIndex(newStageIndex);
          setTargetStage(targetStage, false, newStageIndex, 'scroll');
        }
      }
    });
  };

  const animateToStage2 = (source) => {
    if (!grids.stage2) {
      console.error('❌ Stage 2 grid not available');
      return;
    }

    setAnimationPhase('toStage2');
    
    const targetGrid = grids.stage2;
    const maxSteps = iconIds.length - 1;
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= maxSteps || !targetGrid) {
        setGlyphGrid(targetGrid);
        setAnimationPhase('idle');

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const newGrid = targetGrid.map((row, y) =>
        row.map((targetIndex, x) => {
          const currentIndex = glyphStateRef.current[y]?.[x] || 0;
          
          if (currentIndex < targetIndex) {
            return Math.min(currentIndex + 1, targetIndex);
          } else if (currentIndex > targetIndex) {
            return Math.max(currentIndex - 1, targetIndex);
          } else {
            return currentIndex;
          }
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Determine speed based on current stage - if coming from stage 2 or 3, use slower speed
    let animationSpeed;
    if (currentStageRef.current >= 2) {
      // Coming from stage 2 or 3 (stage1 or stage2), use slower speed for stage 3 <-> 4 <-> 5 transitions
      animationSpeed = STAGE3_TO_5_SPEED;
    } else {
      // Coming from blank or g11, use normal speed
      animationSpeed = STAGE_TRANSITION_SPEED;
    }
    
    animationRef.current = setInterval(animate, animationSpeed);
  };

  return (
    <div>
      {/* Trigger element for ScrollTrigger */}
      <div 
        ref={triggerRef}
        style={{
          height: '250vh', // Use simple vh units instead of calc
          width: '100%',
          position: 'relative'
        }}
      >
        {/* Sticky container that will be pinned */}
        <div 
          ref={stickyContainerRef}
          style={{
            position: 'sticky',
            top: 0,
            width: '100%',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Canvas with sticky behavior */}
          <div className="pixel-scroll-canvas-container">
            <canvas
              ref={canvasRef}
              className="pixel-canvas-responsive pixel-scroll-canvas"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
